import { useMemo, type FormEvent, type ReactElement } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BrowseFamiliesTableBody } from '@/features/families/components/BrowseFamiliesTableBody';
import { Input } from '@/components/ui/input';
import {
  DataTable,
  DataTableToolbar,
  type SortDirection,
} from '@/components/ui/data-table';
import { useClientListPagination } from '@/hooks/useClientListPagination';
import type { FamilySortKey } from '@/features/families/lib/sortFamilySummaries';
import type { FamilySummaryViewModel } from '@/models/family';

export interface BrowseFamiliesPanelProps {
  readonly q: string;
  readonly onQueryChange: (q: string) => void;
  readonly families: readonly FamilySummaryViewModel[];
  readonly sortKey: FamilySortKey | null;
  readonly sortDir: SortDirection;
  readonly onSortChange: (key: FamilySortKey) => void;
}

export function BrowseFamiliesPanel({
  q,
  onQueryChange,
  families: list,
  sortKey,
  sortDir,
  onSortChange,
}: BrowseFamiliesPanelProps): ReactElement {
  const { t } = useTranslation();
  const filterKey = useMemo(
    () => [q, sortKey ?? '', sortDir].join('\0'),
    [q, sortKey, sortDir],
  );
  const pagination = useClientListPagination({ items: list, filterKey });
  const {
    total,
    pageItems,
    from,
    to,
    onPageSizeChange,
    onPagePrevious,
    onPageNext,
    canGoPrevious,
    canGoNext,
    pageSize,
  } = pagination;

  function onSearchFormSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={onSearchFormSubmit}
        className="flex flex-wrap items-end gap-3"
        role="search"
        aria-label={t('routes.browseFamilies.searchAria')}
      >
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-8 h-9 bg-card border-border text-sm"
            placeholder={t('routes.browseFamilies.searchPlaceholder')}
            value={q}
            onChange={(e) => {
              onQueryChange(e.target.value);
            }}
            name="q"
            aria-label={t('routes.browseFamilies.searchPlaceholder')}
          />
        </div>
      </form>

      {total === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('routes.browseFamilies.empty')}
          </p>
        </div>
      ) : (
        <DataTable aria-label={t('routes.browseFamilies.tableRegion')}>
          <DataTableToolbar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            from={from}
            to={to}
            total={total}
            onPagePrevious={onPagePrevious}
            onPageNext={onPageNext}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            pageSizeSelectId="browse-families-page-size"
          />
          <BrowseFamiliesTableBody
            families={pageItems}
            emptyCell={t('patient.demographics.emptyValue')}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={onSortChange}
          />
        </DataTable>
      )}
    </div>
  );
}
